"use client"

import React from "react"

import { ENTITY_ICONS } from "@/constants/entity-icons"
import { useTestSetsList } from "@/test-set/catalog"
import { Input, RadioGroup, RadioGroupItem } from "@coval/ui"

import { EntitySelectionCard } from "@/components/entity-picker/EntitySelectionCard"
import { useEntityPickerDraft } from "@/components/entity-picker/useEntityPickerDraft"
import type { JsonObject } from "@/types/json"

import { TestCasePicker } from "./TestCasePicker"
import { TestSetPickerDialog } from "./TestSetPickerDialog"
import {
  getInitialTestCaseMode,
  getSubSampleInputBlurResult,
  getSubSampleInputChangeResult,
  getSyncedTestCaseMode,
  getTestCaseModeTransition,
  TEST_CASE_MODE,
  type TestCaseMode,
} from "./testSetSelectorState"

const TestSetIcon = ENTITY_ICONS["test-sets"]

interface SimpleTestSetFeatureProps {
  testSetIds?: string[]
  shouldSubSample?: boolean
  subSampleSize?: number
  testCaseParamsByIteration?: JsonObject[]
  disabled?: boolean
  singleSelect?: boolean
  onTestSetChange?: (testSetIds: string[]) => void
  onSubSamplingChange?: (
    shouldSubSample: boolean,
    subSampleSize?: number,
  ) => void
  selectedTestCaseIds?: string[]
  onSelectedTestCaseIdsChange?: (ids: string[]) => void
  onTestCaseParamsChange?: (params: JsonObject[]) => void
  onValidationChange?: (isValid: boolean, errors: string[]) => void
  tagColorMap?: Record<string, string | null | undefined>
  /**
   * Optional action offered inside the test set picker, for a caller that can
   * create one, so a user with no test sets is not stuck at an empty catalog.
   */
  footer?: React.ReactNode
}

export function TestSetSelector({
  testSetIds = [],
  shouldSubSample = false,
  subSampleSize,
  disabled = false,
  singleSelect = false,
  onTestSetChange,
  onSubSamplingChange,
  selectedTestCaseIds = [],
  onSelectedTestCaseIdsChange,
  tagColorMap,
  footer,
}: SimpleTestSetFeatureProps) {
  const { data: testSetRecords, isLoading: testSetsLoading } = useTestSetsList()

  const hasTestSets = testSetIds.length > 0
  const hasMultipleTestSets = testSetIds.length > 1
  const firstTestSetId = testSetIds[0] || ""
  const hasSpecificTestCases = selectedTestCaseIds.length > 0

  const handleTestSetValuesChange = React.useCallback(
    (ids: string[]) => {
      const next = singleSelect ? ids.slice(-1) : ids
      onTestSetChange?.(next)
    },
    [singleSelect, onTestSetChange],
  )

  const picker = useEntityPickerDraft(testSetIds, handleTestSetValuesChange, {
    disabled,
  })

  // Chips follow the committed selection order, and an id with no matching
  // record still gets one so it can be removed.
  const chips = React.useMemo(
    () =>
      testSetIds.map((id) => {
        const testSet = (testSetRecords ?? []).find((ts) => ts.id === id)
        return {
          id,
          label: testSet?.display_name || id,
          isMissing: !testSet && !testSetsLoading,
        }
      }),
    [testSetIds, testSetRecords, testSetsLoading],
  )

  const handleRemoveTestSet = React.useCallback(
    (testSetId: string) => {
      handleTestSetValuesChange(testSetIds.filter((id) => id !== testSetId))
    },
    [handleTestSetValuesChange, testSetIds],
  )

  const [testCaseMode, setTestCaseMode] = React.useState<TestCaseMode>(() =>
    getInitialTestCaseMode(hasSpecificTestCases, shouldSubSample),
  )

  React.useEffect(() => {
    setTestCaseMode((currentMode) => {
      const nextMode = getSyncedTestCaseMode(
        currentMode,
        hasSpecificTestCases,
        shouldSubSample,
      )
      if (
        nextMode === TEST_CASE_MODE.ALL &&
        currentMode !== TEST_CASE_MODE.ALL
      ) {
        onSelectedTestCaseIdsChange?.([])
        onSubSamplingChange?.(false, undefined)
      }
      return nextMode
    })
  }, [
    hasSpecificTestCases,
    shouldSubSample,
    onSelectedTestCaseIdsChange,
    onSubSamplingChange,
  ])

  const handleTestCaseModeChange = React.useCallback(
    (mode: string) => {
      const nextMode = mode as TestCaseMode
      setTestCaseMode(nextMode)
      const transition = getTestCaseModeTransition(nextMode, subSampleSize)
      if (transition.shouldClearSelectedTestCaseIds) {
        onSelectedTestCaseIdsChange?.([])
      }
      onSubSamplingChange?.(
        transition.subSamplingUpdate.shouldSubSample,
        transition.subSamplingUpdate.subSampleSize,
      )
    },
    [onSubSamplingChange, onSelectedTestCaseIdsChange, subSampleSize],
  )

  const [subSampleInputValue, setSubSampleInputValue] = React.useState(
    subSampleSize?.toString() || "",
  )

  React.useEffect(() => {
    setSubSampleInputValue(subSampleSize?.toString() || "")
  }, [subSampleSize])

  const handleSubSampleSizeChange = (value: string) => {
    const result = getSubSampleInputChangeResult(value)
    setSubSampleInputValue(result.cleanValue)
    if (result.subSamplingUpdate) {
      onSubSamplingChange?.(
        result.subSamplingUpdate.shouldSubSample,
        result.subSamplingUpdate.subSampleSize,
      )
    }
  }

  const handleSubSampleSizeBlur = () => {
    const result = getSubSampleInputBlurResult(subSampleInputValue)
    if (!result) {
      return
    }
    setSubSampleInputValue(result.nextInputValue)
    onSubSamplingChange?.(
      result.subSamplingUpdate.shouldSubSample,
      result.subSamplingUpdate.subSampleSize,
    )
  }

  return (
    <>
      <EntitySelectionCard
        icon={TestSetIcon}
        title="Test Set"
        chips={chips}
        onRemove={handleRemoveTestSet}
        onBrowse={picker.open}
        onClearAll={() => handleTestSetValuesChange([])}
        browseLabel="Browse Test Sets"
        addLabel="Add Test Sets"
        browseTestId="test-set-browse-button"
        disabled={disabled}
      >
        {/* Nothing to scope until there is a test set to scope. */}
        {hasTestSets && (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-coval-text">
              Test Cases
            </label>
            <RadioGroup
              value={hasMultipleTestSets ? TEST_CASE_MODE.ALL : testCaseMode}
              onValueChange={handleTestCaseModeChange}
              disabled={disabled || hasMultipleTestSets}
            >
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <RadioGroupItem value={TEST_CASE_MODE.ALL} />
                Run all test cases
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <RadioGroupItem
                  value={TEST_CASE_MODE.SELECT}
                  disabled={hasMultipleTestSets}
                />
                Select specific test cases
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <RadioGroupItem value={TEST_CASE_MODE.SUBSAMPLE} />
                Randomly subsample
              </label>
            </RadioGroup>

            {testCaseMode === TEST_CASE_MODE.SUBSAMPLE && (
              <div className="space-y-1">
                <Input
                  type="number"
                  placeholder="Number of test cases"
                  value={subSampleInputValue}
                  onChange={(e) => handleSubSampleSizeChange(e.target.value)}
                  onBlur={handleSubSampleSizeBlur}
                  disabled={disabled}
                  aria-label="Sample size"
                />
                <p className="text-xs text-coval-text-muted">
                  Random subset of test cases to evaluate
                </p>
              </div>
            )}
          </div>
        )}

        {hasTestSets &&
          testCaseMode === TEST_CASE_MODE.SELECT &&
          firstTestSetId &&
          !hasMultipleTestSets && (
            <TestCasePicker
              testSetId={firstTestSetId}
              selectedTestCaseIds={selectedTestCaseIds}
              onSelectedTestCaseIdsChange={(ids) =>
                onSelectedTestCaseIdsChange?.(ids)
              }
              disabled={disabled}
            />
          )}
      </EntitySelectionCard>
      <TestSetPickerDialog
        isOpen={picker.isOpen}
        onCancel={picker.cancel}
        onApply={picker.apply}
        testSets={testSetRecords ?? []}
        isLoading={testSetsLoading}
        selectedIds={picker.draftIds}
        onSelectionChange={picker.setDraftIds}
        allowMultiple={!singleSelect}
        tagColorMap={tagColorMap}
        createAction={footer}
      />
    </>
  )
}
